/**
 * The EN 10204 classification of a file (wave b12 frontend item 9; wave CRED ruling 19):
 * the line under a file that says what inspection document it is, and the dialog that
 * says it (PUT /api/attachments/{id}/classification).
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * The server lets the file's uploader or the operator classify it (404 to anyone else).
 * A 3.2 certificate names the inspection body that validated it, a third party
 * inspection agency (422 evidence.inspection_body_required); a file of an ended
 * contract's record is 409 evidence.contract_state; the same classification again 409
 * evidence.unchanged. Each refusal is said inside the dialog in the server's words.
 */

import { useState } from "react";

import { ApiError } from "../api/client";
import { DOCUMENT_TYPES, classifyAttachment, type Classification } from "../api/credentials";
import { listParties } from "../api/portfolio";
import { useToken } from "../auth/sessionContext";
import { strings } from "../brand/strings";
import { ServerField } from "../lib/serverErrors";
import { ActionDialog } from "../nav/ActionDialog";
import { useQuery } from "../screens/useQuery";

/** The party type of an inspection body that may validate a 3.2 certificate. */
const INSPECTION_BODY_TYPE = "TPI_AGENCY";

export function ClassificationLine({
  attachmentId,
  filename,
  classification,
  mayClassify,
  onClassified,
}: {
  readonly attachmentId: string;
  readonly filename: string;
  readonly classification: Classification | null;
  /** The uploader or the operator: the act is offered; the server still decides. */
  readonly mayClassify: boolean;
  readonly onClassified: (message: string) => void;
}): React.JSX.Element | null {
  const copy = strings.credentials;
  const [open, setOpen] = useState(false);
  if (classification === null && !mayClassify) {
    return null;
  }
  return (
    <span className="q-ui inline-flex flex-wrap items-center gap-2" data-testid="classification">
      {classification !== null && (
        <span className="q-chip" data-testid="classification-chip">
          {copy.classification(
            copy.documentType[classification.documentType] ?? classification.documentType,
            classification.heatOrLot,
          )}
        </span>
      )}
      {mayClassify && (
        <button
          type="button"
          className="q-button q-button--quiet"
          data-testid="classification-open"
          onClick={() => {
            setOpen(true);
          }}
        >
          {copy.classify}
        </button>
      )}
      {open && (
        <ClassificationDialog
          attachmentId={attachmentId}
          filename={filename}
          classification={classification}
          onClose={() => {
            setOpen(false);
          }}
          onDone={() => {
            setOpen(false);
            onClassified(copy.classified(filename));
          }}
        />
      )}
    </span>
  );
}

function ClassificationDialog({
  attachmentId,
  filename,
  classification,
  onClose,
  onDone,
}: {
  readonly attachmentId: string;
  readonly filename: string;
  readonly classification: Classification | null;
  readonly onClose: () => void;
  readonly onDone: () => void;
}): React.JSX.Element {
  const token = useToken();
  const copy = strings.credentials;
  const parties = useQuery((signal) => listParties(token, signal), [token]);
  const [documentType, setDocumentType] = useState(classification?.documentType ?? "");
  const [heat, setHeat] = useState(classification?.heatOrLot ?? "");
  const [body, setBody] = useState(classification?.inspectionBodyPartyId ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const needsBody = documentType === "EN10204_3_2";
  const bodies =
    parties.kind === "ready"
      ? parties.data.filter((one) => one.partyTypeCode === INSPECTION_BODY_TYPE)
      : [];
  const ready = documentType !== "" && (!needsBody || body !== "");

  async function send(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await classifyAttachment(token, attachmentId, {
        document_type: documentType,
        heat_or_lot: heat.trim() === "" ? null : heat.trim(),
        inspection_body_party_id: needsBody ? body : null,
      });
      onDone();
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <ActionDialog open title={copy.classifyHeading(filename)} onClose={onClose} error={error}>
      <p className="q-shell__lede">{copy.classifyLede}</p>
      <div className="q-field">
        <label className="q-field__label" htmlFor="classification-type">
          {copy.documentTypeLabel}
        </label>
        <ServerField field="document_type">
          <select
            id="classification-type"
            className="q-input"
            data-testid="classification-type"
            value={documentType}
            onChange={(event) => {
              setDocumentType(event.target.value);
            }}
          >
            <option value="" />
            {DOCUMENT_TYPES.map((one) => (
              <option key={one} value={one}>
                {copy.documentType[one]}
              </option>
            ))}
          </select>
        </ServerField>
      </div>
      <div className="q-field">
        <label className="q-field__label" htmlFor="classification-heat">
          {copy.heatLabel}
        </label>
        <ServerField field="heat_or_lot">
          <input
            id="classification-heat"
            className="q-input"
            data-testid="classification-heat"
            value={heat}
            onChange={(event) => {
              setHeat(event.target.value);
            }}
          />
        </ServerField>
      </div>
      {needsBody && (
        <div className="q-field">
          <label className="q-field__label" htmlFor="classification-body">
            {copy.bodyLabel}
          </label>
          <ServerField field="inspection_body_party_id">
            <select
              id="classification-body"
              className="q-input"
              data-testid="classification-body"
              aria-required="true"
              value={body}
              onChange={(event) => {
                setBody(event.target.value);
              }}
            >
              <option value="">{copy.bodyChoose}</option>
              {bodies.map((one) => (
                <option key={one.id} value={one.id}>
                  {one.name}
                </option>
              ))}
            </select>
          </ServerField>
          {body === "" && (
            <p className="q-note" data-testid="classification-body-needed">
              {copy.bodyNeeded}
            </p>
          )}
        </div>
      )}
      <div className="q-actions-row">
        <button
          type="button"
          className="q-button q-mini--primary"
          data-testid="classification-send"
          disabled={busy || !ready}
          onClick={() => {
            void send();
          }}
        >
          {copy.classifySend}
        </button>
        <button type="button" className="q-button q-button--quiet" onClick={onClose}>
          {strings.common.cancel}
        </button>
      </div>
    </ActionDialog>
  );
}
